import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { faPlug, faShieldCheck, faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Icon } from "@/components/Icon/Icon";
import { Spinner } from "@/components/Spinner/Spinner";
import {
	useDecideMcpConsentMutation,
	useGetMcpConsentInfoQuery,
} from "@/pages/Private/redux/mcpOauth/mcpOauth.api";
import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useAppSelector } from "@/redux/hooks";
import { ROUTE_CONFIG } from "@/routes/config";

import { consentErrorMessageKey, resolveConsentError } from "./consentError";
import { rememberPendingConsent } from "./pendingConsent";

export const MCP_CONSENT_REQUEST_PARAM = "request_id";

/**
 * The human step of the MCP OAuth flow (docs/brief-mcp-server.md §"Tier 4"). `GET
 * /api/oauth/authorize` validated the request and sent the browser here with an opaque request id;
 * approving mints the authorization code and hands the browser back to the MCP client.
 *
 * It lives in the public routes so an unauthenticated user can land on it and be sent through the
 * normal login — the request id is remembered so login returns here instead of the dashboard
 * (see pendingConsent.ts).
 */
export const McpConsent: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string, options?: Record<string, unknown>) => t(`mcpConsent.${key}`, options);

	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { user } = useAppSelector(authSelector);

	const requestId = searchParams.get(MCP_CONSENT_REQUEST_PARAM) ?? "";
	const isAuthenticated = !!user;

	const {
		data: consent,
		isLoading,
		isError,
		error,
	} = useGetMcpConsentInfoQuery(requestId, { skip: !requestId || !isAuthenticated });

	const consentError = resolveConsentError(error);

	const [decide, { isLoading: isDeciding }] = useDecideMcpConsentMutation();
	const [decisionError, setDecisionError] = useState(false);

	const handleDecision = async (approve: boolean) => {
		setDecisionError(false);

		try {
			const { redirectUrl } = await decide({ requestId, approve }).unwrap();

			// Hand the browser back to the MCP client — an external origin, so a full navigation.
			window.location.href = redirectUrl;
		} catch {
			setDecisionError(true);
		}
	};

	const handleLogin = () => {
		rememberPendingConsent(requestId);
		navigate(ROUTE_CONFIG.LOGIN);
	};

	return (
		<div className="flex min-h-screen items-center justify-center bg-brand-white px-4 py-10">
			<div className="w-full max-w-md overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
				<div className="flex items-start gap-3 border-b border-gray-100 px-5 py-4">
					<div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[9px] bg-blue-action-200 text-brand-light">
						<Icon className="h-[18px] w-[18px]" icon={faPlug} />
					</div>

					<div className="min-w-0 flex-1">
						<h6 className="text-[15px] font-bold text-text">{ts("title")}</h6>
						<p className="mt-0.5 text-[12.5px] text-text-meta">{ts("subtitle")}</p>
					</div>
				</div>

				<div className="px-5 py-5">
					{!requestId && <p className="text-sm text-text">{ts("missingRequest")}</p>}

					{!!requestId && !isAuthenticated && (
						<div className="flex flex-col gap-4">
							<p className="text-sm text-text">{ts("loginRequired")}</p>

							<Button
								color={ButtonColor.BRAND}
								size={ButtonSize.M}
								title={ts("logIn")}
								onClick={handleLogin}
							/>
						</div>
					)}

					{!!requestId && isAuthenticated && isLoading && (
						<div className="flex justify-center py-6">
							<Spinner color="#4F46E5" />
						</div>
					)}

					{!!requestId && isAuthenticated && isError && (
						<div className="flex flex-col gap-4">
							<p className="text-sm text-text">
								{ts(consentErrorMessageKey(consentError.kind), { status: consentError.status })}
							</p>

							{consentError.kind === "session" && (
								<Button
									color={ButtonColor.BRAND}
									size={ButtonSize.M}
									title={ts("logIn")}
									onClick={handleLogin}
								/>
							)}
						</div>
					)}

					{!!consent && (
						<div className="flex flex-col gap-4">
							<p className="text-sm text-text">{ts("intro", { clientName: consent.clientName })}</p>

							<dl className="flex flex-col gap-2 rounded-lg bg-brand-white px-4 py-3 text-[13px]">
								<div className="flex justify-between gap-3">
									<dt className="text-text-meta">{ts("application")}</dt>
									<dd className="font-semibold text-text">{consent.clientName}</dd>
								</div>

								<div className="flex justify-between gap-3">
									<dt className="text-text-meta">{ts("account")}</dt>
									<dd className="truncate font-semibold text-text">{consent.userEmail}</dd>
								</div>

								<div className="flex justify-between gap-3">
									<dt className="text-text-meta">{ts("company")}</dt>
									<dd className="font-semibold text-text">{consent.companyName}</dd>
								</div>

								{!!consent.redirectHost && (
									<div className="flex justify-between gap-3">
										<dt className="text-text-meta">{ts("redirect")}</dt>
										<dd className="truncate font-semibold text-text">{consent.redirectHost}</dd>
									</div>
								)}
							</dl>

							<div className="flex items-start gap-2 text-[12.5px] text-text-meta">
								<Icon className="mt-0.5 h-4 w-4 flex-shrink-0" icon={faShieldCheck} />
								<span>{ts("permissionNote")}</span>
							</div>

							{!consent.mcpAccessEnabled && (
								<div className="flex items-start gap-2 rounded-lg bg-yellow-50 px-3 py-2 text-[12.5px] text-text">
									<Icon className="mt-0.5 h-4 w-4 flex-shrink-0" icon={faTriangleExclamation} />
									<span>{ts("noMcpAccess")}</span>
								</div>
							)}

							{decisionError && (
								<p className="text-[12.5px] text-red-600">{ts("decisionFailed")}</p>
							)}

							<div className="flex items-center justify-end gap-3">
								<Button
									color={ButtonColor.ACTION_SECONDARY}
									disabled={isDeciding}
									size={ButtonSize.M}
									title={ts("deny")}
									onClick={() => handleDecision(false)}
								/>

								<Button
									color={ButtonColor.BRAND}
									disabled={isDeciding || !consent.mcpAccessEnabled}
									isLoading={isDeciding}
									size={ButtonSize.M}
									title={ts("approve")}
									onClick={() => handleDecision(true)}
								/>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};
